/**
 * Password & UI Utility Functions
 *
 * Pure DOM utilities with zero dependency on puzzle state.
 * Used in profil.php, regisztracio.php, solver pages, puzzles list.
 */

/** Password strength descriptions (Hungarian) */
const PASSWORD_STRENGTH_LEVELS = [
    ['Nagyon gyenge', '#ff0000'], // Very weak
    ['Gyenge', '#cc0000'], // Weak
    ['Jobb', '#ff6c00'], // Better
    ['Közepes', '#ffd200'], // Medium
    ['Jó', '#4cbe0d'], // Good
    ['Erős', '#398b0c'], // Strong
    ['Nagyon erős', '#235508'], // Very strong
] as const;

/**
 * Calculate and display password strength.
 * Hungarian name preserved for PHP template compatibility.
 *
 * @param password - The password to evaluate
 * @param elementId - ID of the element to display the result
 */
export function passwordStrength(password: string, elementId: string): void {
    let score = 0;

    // Length check
    if (password.length > 7) score++;
    if (password.length > 11) score++;

    // Character type checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d+/.test(password)) score++;
    if (/[!@#$%^&*?_~\-()]/.test(password)) score++;

    const element = document.getElementById(elementId);
    if (!element) return;

    const [label, color] = PASSWORD_STRENGTH_LEVELS[score];

    element.innerHTML = label;
    element.style.width = score > 0 ? `${score * 50}px` : '';
    element.style.color = color;
    element.style.borderBottomWidth = '5px';
    element.style.marginTop = '-5px';
    element.style.borderColor = color;
}

/**
 * Check if two password fields match.
 * Hungarian name preserved for PHP template compatibility.
 *
 * @param input - The confirmation password input
 * @param originalInput - The original password input
 */
export function checkPassword(
    input: HTMLInputElement,
    originalInput: HTMLInputElement,
): void {
    if (input.value !== originalInput.value) {
        input.setCustomValidity('Nem egyezik a két jelszó!');
    } else {
        input.setCustomValidity('');
    }
}

/**
 * Toggle visibility of an element and swap the link text.
 * Matches legacy scripts/index.js showOrHide function.
 *
 * @param linkElement - The anchor element that was clicked
 * @param targetId - ID of the element to show/hide
 * @param buttonText - Text to display when element is visible (e.g. "Elrejt")
 */
export function showOrHide(
    linkElement: HTMLElement,
    targetId: string,
    buttonText: string,
): void {
    const target = document.getElementById(targetId);
    if (!target) return;

    if (target.style.display === 'block') {
        target.style.display = 'none';
        linkElement.innerHTML = linkElement.dataset.originalText ?? '';
    } else {
        target.style.display = 'block';
        linkElement.dataset.originalText = linkElement.innerHTML;
        linkElement.innerHTML = buttonText;
    }
}

/**
 * Select or deselect all checkboxes on the page.
 * Hungarian name preserved for PHP template compatibility.
 *
 * @param checkbox - The "select all" checkbox
 */
export function selectAllCheckboxes(checkbox: HTMLInputElement): void {
    const isChecked = checkbox.checked;
    const inputs = document.getElementsByTagName('input');

    for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].type === 'checkbox') {
            inputs[i].checked = isChecked;
        }
    }
}
